import { useCallback, useEffect, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { useTranslation } from 'react-i18next';
import BTButton from '@common-components/bt-button/bt-button';
import { namespace } from '@common/config/constants';
import {
  usePasskeyListMutation,
  usePasskeyRegisterOptionsMutation,
  usePasskeyRegisterVerifyMutation,
  usePasskeyRemoveMutation,
  usePasskeyRenameMutation,
  usePasskeyStatusMutation,
} from '@common-reducers/backend-api/backend-api';
import type { PasskeyFailure, PasskeySummary } from './passkey-types';

type Dialogue =
  | { kind: 'add' }
  | { kind: 'rename'; passkey: PasskeySummary }
  | { kind: 'remove'; passkey: PasskeySummary }
  | null;

const isFailure = (value: unknown): value is PasskeyFailure =>
  typeof value === 'object' && value !== null && 'ok' in value && value.ok === false && 'reason' in value;

const isCancelled = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'name' in error && (error.name === 'NotAllowedError' || error.name === 'AbortError');

/** "Passkeys" block of the account security page. Renders nothing while the backend flag is off for this user. */
export const PasskeysSection = () => {
  const { t } = useTranslation(namespace);
  const [status] = usePasskeyStatusMutation();
  const [list] = usePasskeyListMutation();
  const [registerOptions, { reset: resetOptions }] = usePasskeyRegisterOptionsMutation();
  const [registerVerify] = usePasskeyRegisterVerifyMutation();
  const [rename] = usePasskeyRenameMutation();
  const [remove, { reset: resetRemove }] = usePasskeyRemoveMutation();

  const [enabled, setEnabled] = useState(false);
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([]);
  const [dialogue, setDialogue] = useState<Dialogue>(null);
  const [password, setPassword] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const res = await list().unwrap();
    if (!isFailure(res)) setPasskeys(res.passkeys);
  }, [list]);

  useEffect(() => {
    let alive = true;
    status().unwrap().then(async (res) => {
      if (!alive || isFailure(res) || !res.enabled) return;
      setEnabled(true);
      await reload();
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [status, reload]);

  const reasonText = useCallback((failure: PasskeyFailure): string => {
    switch (failure.reason) {
      case 'wrong-password': return t('accountSecurityView.passkeys.errWrongPassword');
      case 'password-required': return t('accountSecurityView.passkeys.errPasswordRequired');
      case 'rate-limited': return t('accountSecurityView.passkeys.errRateLimited');
      case 'too-many-passkeys': return t('accountSecurityView.passkeys.errTooMany');
      case 'already-registered': return t('accountSecurityView.passkeys.errAlready');
      case 'impersonating': return t('accountSecurityView.passkeys.errImpersonating');
      default: return t('accountSecurityView.passkeys.errGeneric');
    }
  }, [t]);

  const close = () => {
    // RTK keeps the last mutation args (incl. the password) in the store until reset.
    resetOptions();
    resetRemove();
    setDialogue(null);
    setPassword('');
    setDeviceName('');
    setError(null);
  };

  const submit = async () => {
    if (!dialogue) return;
    setBusy(true);
    setError(null);
    try {
      if (dialogue.kind === 'add') {
        const optionsRes = await registerOptions({ password }).unwrap();
        if (isFailure(optionsRes)) { setError(reasonText(optionsRes)); return; }
        const attestation = await startRegistration({ optionsJSON: optionsRes.options });
        const verifyRes = await registerVerify({ response: attestation, ...(deviceName.trim() ? { deviceName: deviceName.trim() } : {}) }).unwrap();
        if (isFailure(verifyRes)) { setError(reasonText(verifyRes)); return; }
        setNotice(t('accountSecurityView.passkeys.added'));
      } else if (dialogue.kind === 'rename') {
        const res = await rename({ passkeyId: dialogue.passkey.id, deviceName: deviceName.trim() }).unwrap();
        if (isFailure(res)) { setError(reasonText(res)); return; }
      } else {
        const res = await remove({ passkeyId: dialogue.passkey.id, password }).unwrap();
        if (isFailure(res)) { setError(reasonText(res)); return; }
        setNotice(t('accountSecurityView.passkeys.removed'));
      }
      close();
      // The change already succeeded; a failed refresh must not be reported as a failure of it.
      await reload().catch(() => undefined);
    } catch (err) {
      setError(isCancelled(err) ? t('accountSecurityView.passkeys.errCancelled') : t('accountSecurityView.passkeys.errGeneric'));
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;
  const supported = browserSupportsWebAuthn();
  const needsPassword = dialogue?.kind === 'add' || dialogue?.kind === 'remove';
  const canSubmit = !busy && (!needsPassword || password.length > 0) && (dialogue?.kind !== 'rename' || deviceName.trim().length > 0);

  return (
    <Stack spacing={2} data-testid="passkeys-section" sx={{ mt: 4 }}>
      <Typography variant="h6" component="h5">{t('accountSecurityView.passkeys.title')}</Typography>
      <Typography variant="body2" color="text.secondary">{t('accountSecurityView.passkeys.description')}</Typography>
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      {passkeys.length === 0 && <Typography variant="body2">{t('accountSecurityView.passkeys.none')}</Typography>}
      {passkeys.map((passkey) => (
        <Stack key={passkey.id} direction="row" alignItems="center" justifyContent="space-between" data-testid="passkey-row"
          sx={{ border: '1px solid var(--color-border, #ddd)', borderRadius: 1, p: 1.5 }}>
          <Stack>
            <Typography variant="body1">{passkey.deviceName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('accountSecurityView.passkeys.createdOn', { date: new Date(passkey.createdAt).toLocaleDateString() })}
            </Typography>
          </Stack>
          <Stack direction="row">
            <IconButton aria-label={`${t('accountSecurityView.passkeys.rename')} ${passkey.deviceName}`} onClick={() => { setDeviceName(passkey.deviceName); setDialogue({ kind: 'rename', passkey }); }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton aria-label={`${t('accountSecurityView.passkeys.remove')} ${passkey.deviceName}`} onClick={() => setDialogue({ kind: 'remove', passkey })}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      ))}
      {supported
        ? <BTButton sx={{ maxWidth: '200px', alignSelf: 'end' }} onClick={() => setDialogue({ kind: 'add' })}>{t('accountSecurityView.passkeys.add')}</BTButton>
        : <Alert severity="info">{t('accountSecurityView.passkeys.unsupported')}</Alert>}

      <Dialog open={dialogue !== null} onClose={busy ? undefined : close} fullWidth maxWidth="xs">
        <DialogTitle>
          {dialogue?.kind === 'add' && t('accountSecurityView.passkeys.add')}
          {dialogue?.kind === 'rename' && t('accountSecurityView.passkeys.rename')}
          {dialogue?.kind === 'remove' && t('accountSecurityView.passkeys.remove')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {dialogue?.kind === 'add' && <Typography variant="body2">{t('accountSecurityView.passkeys.addHelp')}</Typography>}
            {dialogue?.kind === 'remove' && <Typography variant="body2">{t('accountSecurityView.passkeys.removeHelp', { name: dialogue.passkey.deviceName })}</Typography>}
            {(dialogue?.kind === 'add' || dialogue?.kind === 'rename') && (
              <TextField label={t('accountSecurityView.passkeys.deviceName')} value={deviceName} onChange={(e) => setDeviceName(e.target.value)}
                inputProps={{ maxLength: 80 }} disabled={busy} size="small" />
            )}
            {needsPassword && (
              <TextField label={t('accountSecurityView.currentPasswordLabel')} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" disabled={busy} size="small" />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <BTButton btvariant="text" onClick={close} disabled={busy}>{t('accountSecurityView.passkeys.cancel')}</BTButton>
          <BTButton loading={busy} disabled={!canSubmit} onClick={() => { void submit(); }}>{t('accountSecurityView.passkeys.confirm')}</BTButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PasskeysSection;
